// ============================================================================
// StakeGroupedView - CoinPoker-style stake-level lobby with PSS design
// ============================================================================
// Collapses N cash tables into one row per stake-level (e.g. all 0.01/0.02
// tables → a single "Blinds 0.01/0.02" row showing total players + min
// buy-in across that stake). Tapping a row drills into the standard
// table-card grid filtered to that stake.
//
// Two states owned by the parent page:
//   - selectedStake: null → show grouped list
//   - selectedStake: '0.05/0.1' → show tables filtered to that stake
// ============================================================================
import React, { useMemo } from 'react';
import { Box, HStack, VStack, Text } from '@chakra-ui/react';
import { Users as UsersIcon, Coins as CoinsIcon } from 'lucide-react';
import type { TableData } from '../../services/tableService';
import { POKER_COLORS } from '../../theme/designSystem';

export interface StakeKey {
  smallBlind: number;
  bigBlind: number;
  currency: 'playcoins' | 'pkcash';
}

interface StakeGroupedViewProps {
  tables: TableData[];
  currencyLabel: string;        // 'PSS Coins' or 'PlayCoins'
  currencySymbol?: string;       // '$' for cash, '' for play coins
  onSelectStake: (key: StakeKey) => void;
}

interface StakeGroup {
  key: string;
  smallBlind: number;
  bigBlind: number;
  currency: 'playcoins' | 'pkcash';
  playerCount: number;
  tableCount: number;
  minBuyIn: number;
}

/**
 * Group an array of tables by (currency, smallBlind, bigBlind). Returns
 * the groups sorted from lowest BB to highest BB so micro stakes appear
 * first.
 */
function groupTablesByStake(tables: TableData[]): StakeGroup[] {
  const map = new Map<string, StakeGroup>();
  for (const t of tables) {
    const sb = t.smallBlind || 0;
    const bb = t.bigBlind || 0;
    const currency = (t as any).currency || 'playcoins';
    const key = `${currency}|${sb}|${bb}`;
    const existing = map.get(key);
    const players = Array.isArray(t.players) ? t.players.length : 0;
    if (existing) {
      existing.playerCount += players;
      existing.tableCount += 1;
      if (typeof t.minBuyIn === 'number' && t.minBuyIn < existing.minBuyIn) {
        existing.minBuyIn = t.minBuyIn;
      }
    } else {
      map.set(key, {
        key,
        smallBlind: sb,
        bigBlind: bb,
        currency,
        playerCount: players,
        tableCount: 1,
        minBuyIn: t.minBuyIn || 0,
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => a.bigBlind - b.bigBlind);
}

const StakeRow: React.FC<{
  group: StakeGroup;
  currencyLabel: string;
  currencySymbol?: string;
  onClick: () => void;
}> = ({ group, currencyLabel, currencySymbol = '', onClick }) => {
  const fmt = (n: number) => {
    if (n < 1) return n.toFixed(2);
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toLocaleString();
  };

  return (
    <Box
      onClick={onClick}
      cursor="pointer"
      bg="rgba(20, 30, 42, 0.85)"
      border="1px solid"
      borderColor={`${POKER_COLORS.cyan}22`}
      borderRadius="xl"
      px={4}
      py={3}
      transition="all 0.15s ease"
      _hover={{
        bg: 'rgba(30, 45, 60, 0.9)',
        borderColor: `${POKER_COLORS.cyan}55`,
        transform: 'translateY(-1px)',
        boxShadow: `0 6px 20px rgba(0,0,0,0.4), 0 0 0 1px ${POKER_COLORS.cyan}33`,
      }}
      _active={{ transform: 'translateY(0)' }}
    >
      <HStack justify="space-between" align="center" spacing={4}>
        {/* Blinds pill */}
        <Box
          minW="140px"
          bg="rgba(16, 185, 129, 0.12)"
          border="1px solid"
          borderColor="rgba(16, 185, 129, 0.5)"
          borderRadius="2xl"
          px={4}
          py={2}
          textAlign="center"
          boxShadow="inset 0 0 20px rgba(16, 185, 129, 0.08)"
        >
          <Text fontSize="2xs" color="rgba(16, 185, 129, 0.85)" fontWeight="bold" letterSpacing="wider">
            BLINDS
          </Text>
          <Text fontSize="md" color="white" fontWeight="bold" lineHeight="1.1" mt={0.5} fontFamily="mono">
            {currencySymbol}{fmt(group.smallBlind)} / {currencySymbol}{fmt(group.bigBlind)}
          </Text>
          <Text fontSize="2xs" color="rgba(16, 185, 129, 0.7)" fontWeight="bold" mt={0.5}>
            NLH
          </Text>
        </Box>

        {/* Player count */}
        <VStack spacing={0} align="center" flex={1}>
          <HStack spacing={1.5}>
            <UsersIcon size={14} color={POKER_COLORS.gold} />
            <Text fontSize="xl" color="white" fontWeight="bold" lineHeight="1">
              {group.playerCount}
            </Text>
          </HStack>
          <Text fontSize="2xs" color="gray.400" letterSpacing="wider" textTransform="uppercase" mt={0.5}>
            Playing
          </Text>
        </VStack>

        {/* Min buy-in */}
        <VStack spacing={0} align="center" flex={1}>
          <HStack spacing={1.5}>
            <CoinsIcon size={14} color={POKER_COLORS.cyan} />
            <Text fontSize="xl" color="white" fontWeight="bold" lineHeight="1">
              {currencySymbol}{fmt(group.minBuyIn)}
            </Text>
          </HStack>
          <Text fontSize="2xs" color="gray.400" letterSpacing="wider" textTransform="uppercase" mt={0.5}>
            Min Buy-in
          </Text>
        </VStack>

        {/* Table count */}
        <VStack spacing={0} align="center" minW="56px">
          <Text fontSize="md" color={POKER_COLORS.cyan} fontWeight="bold" lineHeight="1">
            {group.tableCount}
          </Text>
          <Text fontSize="2xs" color="gray.400" letterSpacing="wider" textTransform="uppercase" mt={0.5}>
            {group.tableCount === 1 ? 'Table' : 'Tables'}
          </Text>
        </VStack>
      </HStack>
      <Text fontSize="2xs" color="gray.600" textAlign="center" mt={2}>
        Tap to view {currencyLabel.toLowerCase()} tables at this stake
      </Text>
    </Box>
  );
};

const StakeGroupedView: React.FC<StakeGroupedViewProps> = ({
  tables,
  currencyLabel,
  currencySymbol,
  onSelectStake,
}) => {
  const groups = useMemo(() => groupTablesByStake(tables), [tables]);

  if (groups.length === 0) {
    return (
      <Box py={8} textAlign="center" color="gray.500">
        <Text fontSize="sm">No {currencyLabel.toLowerCase()} cash tables right now.</Text>
      </Box>
    );
  }

  return (
    <VStack spacing={3} align="stretch">
      {groups.map(group => (
        <StakeRow
          key={group.key}
          group={group}
          currencyLabel={currencyLabel}
          currencySymbol={currencySymbol}
          onClick={() =>
            onSelectStake({
              smallBlind: group.smallBlind,
              bigBlind: group.bigBlind,
              currency: group.currency,
            })
          }
        />
      ))}
    </VStack>
  );
};

export default StakeGroupedView;
